import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useFetch } from '@/modules/core/composables/useFetch'
import {
  Company,
  EntityExpanded,
  EntityInsights,
  Industry,
  Investment,
  Investor,
  InvestorType,
  TermSheet,
} from './better-entity-store'
import { AccreditationStatus } from '@/modules/screening/types'
import { stringifyKey } from '@/modules/core/stores/kv'
import type { LinkedinLookupResult, LinkedinLookupError } from '@/modules/connecting/types'
export interface Shareholder {
  id: string
  cid: string
  name: string
  type: string
}

export interface IdentityLog {
  id: string
  aml_status: string
  is_whitelisted: boolean
  match_status: string
  share_url: string
  updated_at: string
}

export interface Role {
  id: string
  group_id: string
  group_name: string
  group_slug: string
  role_name: string
}

export enum BeneficiaryTypes {
  Individual = 'individual',
  Organization = 'organization',
  Trust = 'trust',
}
export interface Individual {
  id: string
  accredited_status: string
  companies: Company[]
  currency: string
  disbursement_method: string
  disbursement_method_confirmed_at: string
  disbursement_method_confirmed_by: string
  disbursement_wire_bank_name: string
  disbursement_wire_bank_address: string
  disbursement_wire_bank_routing_number: string
  disbursement_wire_bank_account_number: string
  disbursement_wire_bank_swift_code: string
  disbursement_wire_account_name: string
  disbursement_wire_for_further_credit_to: string
  disbursement_wire_bank_notes: string
  disbursement_check_receiver_name: string
  disbursement_other_details: string
  email: string
  entity_ids: string[]
  filing_folder_root_id: string
  industries: Industry[]
  initials: string
  insights: any
  last_login: string
  membership_agreement_data: any
  name: string
  other_entity_ids: string[]
  onboarding_completed_at: string
  onboarding_current_step: string
  profile_picture: string
  profile_status: string
  roles: Role[]
  screening_accreditation_id: string
  screening_accreditation_status: AccreditationStatus
  screening_check_id: string
  screening_check_status: string
}

export interface OtherEntityExpanded {}
export interface Invoice {
  id: string
  marked_as_paid: boolean
}
export interface Beneficiary {
  id: string
  uuid: string
  benefactor_id: string
  group_id: string
  allocation_percentage: number
  birthdate: string
  name: string
  relationship: string
  tax_id: string
  type: BeneficiaryTypes
}
export interface Contact {
  id: string
  date: string
  email: string
  name: string
}
export interface TaxDocument {
  id: string
  shareholder: {
    id: string
    name: string
    type: string
  }
  tax_year: number
}
export interface IndividualExpanded extends Individual {
  beneficiaries: Beneficiary[]
  contacts: Contact[]
  roles: Role[]
  entities: EntityExpanded[]
  groups: { id: string; name: string; logo: string }[]
  investments: Investment[]
  invoices: Invoice[]
  other_entities: OtherEntityExpanded[]
  registration_token: { token: string; sent_at: string }
  shareholders: Shareholder[]
  tax_documents: TaxDocument[]
  term_sheets: TermSheet[]
  user: { id: string; created_at: string }
}

const universities = ref([])

export const useIndividualStore = defineStore('individualStore', () => {
  const individual = ref<IndividualExpanded>(null)

  /////////////////////////////////////////////////////////////////////////////
  // Actions
  /////////////////////////////////////////////////////////////////////////////

  const addIndividual = async (payload, cid: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/individuals`).post(payload)
  }

  const addGroupAdmin = async (payload, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/group-admins`).post(payload)
  }

  const fetchIndividual = async (
    id: string,
    { slug, shareholder_cids }: { slug: string; shareholder_cids?: string[] },
  ) => {
    const params = new URLSearchParams()
    if (!!shareholder_cids) {
      params.set('shareholder_cids', shareholder_cids.join(','))
    }

    const queryParams = params.toString() ? `?${params}` : ''
    const data = await useFetch(`/v4/${slug}/investing/individuals/${id}${queryParams}`).get<IndividualExpanded>()
    individual.value = data
  }

  const fetchEntities = async (
    entityIds: string[],
    { slug, shareholder_cids }: { slug: string; shareholder_cids?: string[] },
  ) => {
    const params = new URLSearchParams()
    if (!!shareholder_cids) {
      params.set('shareholder_cids', shareholder_cids.join(','))
    }

    const queryParams = params.toString() ? `?${params}` : ''
    try {
      individual.value.entities = await Promise.all(
        entityIds.map(async (entityId) => {
          const [entity, insights] = await Promise.all([
            useFetch(`/v4/${slug}/investing/investor-sets/${entityId}${queryParams}`).get<EntityExpanded>(),
            useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/insights${queryParams}`).get<EntityInsights>(),
          ])
          return {
            ...entity,
            insights: insights.insights,
            structure: insights.structure,
            document_status: insights.document_status,
          }
        }),
      )
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const fetchOtherEntities = async (
    otherEntityIds: string[],
    { slug, shareholder_cids }: { slug: string; shareholder_cids?: string[] },
  ) => {
    const params = new URLSearchParams()
    if (!!shareholder_cids) {
      params.set('shareholder_cids', shareholder_cids.join(','))
    }

    const queryParams = params.toString() ? `?${params}` : ''
    try {
      individual.value.other_entities = await Promise.all(
        otherEntityIds.map((otherEntityId) =>
          useFetch(`/v4/${slug}/investing/other-entities/${otherEntityId}${queryParams}`).get<OtherEntityExpanded>(),
        ),
      )
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const fetchInvoices = async (
    individualId: string,
    { slug, shareholder_cids }: { slug: string; shareholder_cids?: string[] },
  ) => {
    const params = new URLSearchParams()
    if (!!shareholder_cids) {
      params.set('shareholder_cids', shareholder_cids.join(','))
    }

    const queryParams = params.toString() ? `?${params}` : ''
    const data = await useFetch(`/v4/${slug}/investing/individuals/${individualId}/invoices${queryParams}`).get<
      Invoice[]
    >()
    individual.value.invoices = data
  }

  const fetchInvestments = async (
    individualId: string,
    { slug, shareholder_cids, date }: { slug: string; shareholder_cids?: string[]; date?: string },
  ) => {
    const params = new URLSearchParams()
    if (shareholder_cids) params.set('shareholder_cids', shareholder_cids.join(','))
    if (date) params.set('date', date)

    const queryParams = params.toString() ? `?${params}` : ''
    const investments = await useFetch(
      `/v4/${slug}/investing/individuals/${individualId}/investments${queryParams}`,
    ).get<Investment[]>()
    individual.value.investments = investments
  }

  const fetchBeneficiaries = async (individualId: string, { slug }: { slug: string }) => {
    const data = await useFetch(`/v4/${slug}/investing/individuals/${individualId}/beneficiaries`).get<Beneficiary[]>()
    individual.value.beneficiaries = data
  }

  const fetchContacts = async (individualId: string, { slug }: { slug: string }) => {
    const data = await useFetch(`/v4/${slug}/investing/individuals/${individualId}/contacts`).get<Contact[]>()
    individual.value.contacts = data
  }

  const fetchRoles = async (individualId: string, { slug }: { slug: string }) => {
    const data = await useFetch(`/v4/${slug}/auth/individuals/${individualId}/roles`).get<Role[]>()
    individual.value.roles = data
  }

  const fetchTaxDocuments = async (
    individualId: string,
    { slug, shareholder_cids }: { slug: string; shareholder_cids?: string[] },
  ) => {
    const params = new URLSearchParams()
    if (!!shareholder_cids) {
      params.set('shareholder_cids', shareholder_cids.join(','))
    }

    const queryParams = params.toString() ? `?${params}` : ''
    const data = await useFetch(`/v4/${slug}/tax/individuals/${individualId}/documents${queryParams}`).get<
      TaxDocument[]
    >()
    individual.value.tax_documents = data
  }

  const incrementOnboardingStep = async (individualId: string, currentStep: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/individuals/${individualId}/increment-onboarding-step`).post({
      current_step: currentStep,
    })
  }

  const incrementConnectionsOnboardingStep = async (individualId: string, currentStep: string) => {
    await useFetch(`/v4/connections/connecting/individuals/${individualId}/increment-onboarding-step`).post({
      current_step: currentStep,
    })
  }

  const lookupLinkedin = async (individualId: string, linkedinUrl: string): Promise<LinkedinLookupResult | null> => {
    try {
      const response = await useFetch(`/v4/connections/connecting/individuals/${individualId}/lookup-linkedin`).post<
        LinkedinLookupResult | LinkedinLookupError
      >({ linkedin_url: linkedinUrl })

      if ('error' in response) {
        return null
      }

      return response
    } catch {
      return null
    }
  }

  const updateDisbursementPreferences = async (individualId: string, payload: any, { slug }: { slug: string }) => {
    const data = await useFetch(
      `/v4/${slug}/investing/individuals/${individualId}/update-disbursement-preferences`,
    ).post(payload)
  }

  const updateRole = async (individualId: string, payload: any, { slug }: { slug: string }) => {
    const data = await useFetch(`/v4/${slug}/auth/individuals/${individualId}/roles/update`).post(payload)
  }

  function jsonToFormData(jsonObject) {
    const formData = new FormData()

    Object.keys(jsonObject).forEach((key) => {
      if (jsonObject[key] !== null && jsonObject[key] !== undefined) {
        if (jsonObject[key] instanceof File) {
          formData.append(key, jsonObject[key])
        } else {
          formData.append(key, jsonObject[key])
        }
      }
    })

    return formData
  }

  const updatePersonalInformation = async (individualId: string, payload: any, { slug }: { slug: string }) => {
    payload = jsonToFormData(payload)
    const data = await useFetch(`/v4/${slug}/investing/individuals/${individualId}/update-personal-information`).post(
      payload,
    )
  }

  const updateInvestmentPreferences = async (individualId: string, payload: any, { slug }: { slug: string }) => {
    const data = await useFetch(`/v4/${slug}/investing/individuals/${individualId}/update-investment-preferences`).post(
      payload,
    )
  }

  const resendInvitation = async (individualId: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/individuals/${individualId}/resend-invitation`).post()
  }

  /////////////////////////////////////////////////////////////////////////////
  // Getters
  /////////////////////////////////////////////////////////////////////////////

  const createGet = <T>(key: string) => {
    return (id: string): T => {
      return individual.value?.[key]?.find((item: T) => item.id.toString() === id.toString())
    }
  }
  const createList = <T>(key: string): T[] => individual.value?.[key] ?? []
  const createComputedList = <T>(key: string) => computed<T[]>(() => createList<T>(key))

  ///////////////////////////////////////////////////////////////////////////////
  // Beneficiaries
  ///////////////////////////////////////////////////////////////////////////////

  const getBeneficiary = createGet<Beneficiary>('beneficiaries')
  const listBeneficiaries = createComputedList<Beneficiary>('beneficiaries')

  ///////////////////////////////////////////////////////////////////////////////
  // Companies
  ///////////////////////////////////////////////////////////////////////////////

  const getCompany = createGet<Company>('companies')
  const listCompanies = createComputedList<Company>('companies')

  ///////////////////////////////////////////////////////////////////////////////
  // Contacts
  ///////////////////////////////////////////////////////////////////////////////

  const getContact = createGet<Contact>('contacts')
  const listContacts = createComputedList<Contact>('contacts')

  ///////////////////////////////////////////////////////////////////////////////
  // Entities
  ///////////////////////////////////////////////////////////////////////////////

  const listEntities = createComputedList<EntityExpanded>('entities')

  ///////////////////////////////////////////////////////////////////////////////
  // Industries
  ///////////////////////////////////////////////////////////////////////////////

  const getIndustry = createGet<Industry>('industries')
  const listIndustries = createComputedList<Industry>('industries')

  ///////////////////////////////////////////////////////////////////////////////
  // Investments
  ///////////////////////////////////////////////////////////////////////////////

  const getInvestment = createGet<Investment>('investments')
  const listInvestments = createComputedList<Investment>('investments')

  ///////////////////////////////////////////////////////////////////////////////
  // Invoices
  ///////////////////////////////////////////////////////////////////////////////

  const getInvoice = createGet<Invoice>('invoices')
  const listInvoices = createComputedList<Invoice>('invoices')

  ///////////////////////////////////////////////////////////////////////////////
  // Roles
  ///////////////////////////////////////////////////////////////////////////////

  const getRole = createGet<Role>('roles')
  const listRoles = createComputedList<Role>('roles')

  ///////////////////////////////////////////////////////////////////////////////
  // Other Entities
  ///////////////////////////////////////////////////////////////////////////////

  const listOtherEntities = createComputedList<EntityExpanded>('other_entities')

  ///////////////////////////////////////////////////////////////////////////////
  // Shareholders
  ///////////////////////////////////////////////////////////////////////////////

  const selectAllShareholderKeys = () => {
    selectedShareholderKeys.value = individual.value?.shareholders.map((s) => stringifyKey([s.id, s.type])) ?? []
  }
  const selectedShareholderKeys = ref<string[]>([])
  const getShareholder = (id: string, type: InvestorType): Investor => {
    return listShareholders.value?.find((item) => item.id === id && item.type === type)
  }
  const listShareholders = createComputedList<Investor>('shareholders')

  ///////////////////////////////////////////////////////////////////////////////
  // TermSheets
  ///////////////////////////////////////////////////////////////////////////////

  const getTermSheet = createGet<TermSheet>('term_sheets')
  const listTermSheets = createComputedList<TermSheet>('term_sheets')

  ///////////////////////////////////////////////////////////////////////////////
  // Tax Documents
  ///////////////////////////////////////////////////////////////////////////////

  const getTaxDocument = createGet<TaxDocument>('tax_documents')
  const listTaxDocuments = createComputedList<TaxDocument>('tax_documents')

  ///////////////////////////////////////////////////////////////////////////////

  return {
    // State
    individual,
    universities,

    // Actions
    addIndividual,
    addGroupAdmin,
    lookupLinkedin,
    fetchIndividual,
    fetchEntities,
    fetchInvestments,
    getInvoice,
    fetchInvoices,
    fetchOtherEntities,
    fetchBeneficiaries,
    fetchContacts,
    fetchRoles,
    fetchTaxDocuments,
    incrementOnboardingStep,
    incrementConnectionsOnboardingStep,
    resendInvitation,
    updateDisbursementPreferences,
    updatePersonalInformation,
    updateInvestmentPreferences,
    updateRole,

    // Getters

    listShareholders,
    getShareholder,
    selectedShareholderKeys,
    selectAllShareholderKeys,

    getBeneficiary,
    listBeneficiaries,

    getCompany,
    listCompanies,

    getContact,
    listContacts,

    getIndustry,
    listIndustries,

    getInvestment,
    listInvestments,

    getTermSheet,
    listTermSheets,

    getTaxDocument,
    listTaxDocuments,

    getRole,
    listRoles,

    listEntities,
    listInvoices,
    listOtherEntities,
  }
})
